// Learning progress: the meal-photo log (streak + Path progress are DERIVED from it),
// Super streak freezes, and where you left off in cook mode.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid/non-secure';
import { persistStorage } from './storage';
import { activeDays, applyFreezes, earnedFreezeWeeks, MAX_FREEZES, repairableDays } from '../logic/streak';
import { startOfWeek } from '../logic/dates';
import type { Day, Id, MealLogEntry, MediaRef } from '../types';

type State = {
  log: MealLogEntry[];
  /** Days kept alive by a freeze or repair (Super). */
  coveredDays: Day[];
  freezes: number;
  /** Monday-weeks that already paid out a freeze. */
  creditedWeeks: Day[];
  pinnedCookbookId?: string;
  /** Cook mode: last step per recipe, cleared when you finish. */
  cookStep: Record<Id, number>;
  updatedAt: number;
};

type Actions = {
  logMeal: (entry: { day: Day; photo: MediaRef; recipeId?: Id; caption?: string }) => MealLogEntry;
  removeMeals: (ids: Id[]) => MealLogEntry[];
  pin: (cookbookId: string | undefined) => void;
  setCookStep: (recipeId: Id, step: number | null) => void;
  /** On launch / day change. Super only: earn freezes, spend them on missed days. */
  settle: (today: Day, isSuper: boolean) => void;
  /** Super: restore a streak that broke within 48 h. Returns false if not repairable. */
  repair: (today: Day) => boolean;
};

export const useProgress = create<State & Actions>()(
  persist(
    (set, get) => ({
      log: [],
      coveredDays: [],
      freezes: 0,
      creditedWeeks: [],
      cookStep: {},
      updatedAt: 0,
      logMeal: e => {
        const entry: MealLogEntry = { id: nanoid(), at: Date.now(), ...e };
        set(s => ({ log: [entry, ...s.log], updatedAt: Date.now() }));
        return entry;
      },
      removeMeals: ids => {
        const removed = get().log.filter(e => ids.includes(e.id));
        set(s => ({ log: s.log.filter(e => !ids.includes(e.id)), updatedAt: Date.now() }));
        return removed;
      },
      pin: id => set({ pinnedCookbookId: id, updatedAt: Date.now() }),
      setCookStep: (recipeId, step) =>
        set(s => {
          const next = { ...s.cookStep };
          if (step === null) delete next[recipeId];
          else next[recipeId] = step;
          return { cookStep: next };
        }),
      settle: (today, isSuper) => {
        if (!isSuper) return;
        const s = get();
        const active = activeDays(s.log);
        const thisWeek = startOfWeek(today);
        // Only finished weeks pay out.
        const newWeeks = earnedFreezeWeeks(active).filter(w => w < thisWeek && !s.creditedWeeks.includes(w));
        let freezes = Math.min(MAX_FREEZES, s.freezes + newWeeks.length);
        const res = applyFreezes(active, new Set(s.coveredDays), freezes, today);
        freezes = res.freezes;
        set({ freezes, coveredDays: [...res.covered], creditedWeeks: [...s.creditedWeeks, ...newWeeks], updatedAt: Date.now() });
      },
      repair: today => {
        const s = get();
        const days = repairableDays(activeDays(s.log), new Set(s.coveredDays), today);
        if (days.length === 0) return false;
        set({ coveredDays: [...s.coveredDays, ...days], updatedAt: Date.now() });
        return true;
      },
    }),
    { name: 'progress', storage: persistStorage, version: 1 },
  ),
);
